import { css, Global } from '@emotion/react';
import { getGenericBasicCards } from '@mythos/common/print/helpers';
import {
  BackInfo,
  CardInfo,
  GenericBasicInfo,
  ReferenceInfo,
  RenderItemInfo,
  TributeInfo,
} from '@mythos/common/print/types';
import { CardAffinity } from '@mythos/game/CardTypes';
import { CardDef, mergeModuleCards, TributeDef } from '@mythos/game/GameModel';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import { ArtResolution, ArtResolutionContext } from './ArtResolution';
import { CardBack, CardBackView } from './CardBackView';
import CardView from './CardView';
import {
  MercenaryReferenceCardView,
  ProbabilityReferenceCardView,
  ScavengeReferenceCardView,
} from './ReferenceCardView';
import TributeCardView from './TributeCardView';
import { useGameDefinitionsStore } from './stores/gameDefinitionsStore';

function PrintCardsPage() {
  const { gameDefinitions, fetchGameDefinitions } = useGameDefinitionsStore(
    useShallow((state) => ({
      gameDefinitions: state.gameDefinitions,
      fetchGameDefinitions: state.fetchGameDefinitions,
    })),
  );
  const [searchParams] = useSearchParams();
  const moduleName = searchParams.get('module');
  const cardFilter = searchParams.get('card');
  // Print pages render the 600 dpi tier by default; ?art=web opts back into
  // the bundled web tier (A/B comparison, or machines without the print tier).
  const artResolution: ArtResolution =
    searchParams.get('art') === 'web' ? 'web' : 'print';

  useEffect(() => {
    fetchGameDefinitions();
  }, [fetchGameDefinitions]);

  if (!gameDefinitions) {
    return <div>Loading...</div>;
  }

  let { cardDefs, tributeCardDefs } = gameDefinitions;
  const { cardDefinitions, tributeCardDefinitions } = mergeModuleCards(
    cardDefs,
    tributeCardDefs,
    [],
  );
  cardDefs = cardDefinitions;
  tributeCardDefs = tributeCardDefinitions;

  if (moduleName) {
    const module = gameDefinitions.modules.find(
      (module) => module.key === moduleName,
    );
    if (!module) {
      throw new Error(`Module ${moduleName} not found`);
    }
    cardDefs = module.cardDefs;
    tributeCardDefs = module.tributeCardDefs;
  }

  const basiccards = cardDefs.filter(
    (card) => card.affinity === CardAffinity.Basic,
  );
  const leadercards = cardDefs.filter(
    (card) => card.affinity === CardAffinity.Leader,
  );
  const age1cards = cardDefs.filter(
    (card) => card.affinity !== CardAffinity.Basic && card.age === 1,
  );
  const age2cards = cardDefs.filter(
    (card) => card.affinity !== CardAffinity.Basic && card.age === 2,
  );
  const age3cards = cardDefs.filter(
    (card) => card.affinity !== CardAffinity.Basic && card.age === 3,
  );
  const scavengeCards = cardDefs.filter((c) => c.name.startsWith('Scavenge'));
  const mercenaryCards = cardDefs.filter((c) => c.name.startsWith('Mercenary'));
  const genericBasics = getGenericBasicCards(cardDefs);

  const cardBackByType: { [key: string]: CardBack } = {
    age1: { id: 'age1back', type: 'age1' },
    age2: { id: 'age2back', type: 'age2' },
    age3: { id: 'age3back', type: 'age3' },
    tribute: { id: 'tributeback', type: 'tribute' },
    basic1: { id: 'basic1back', type: 'basic1' },
    basic2: { id: 'basic2back', type: 'basic2' },
    basic3: { id: 'basic3back', type: 'basic3' },
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  };

  const getCardInfo = (card: CardDef): CardInfo => ({
    type: 'card',
    cardID: card.defID,
    name: card.name,
    age: card.age,
    affinity:
      card.affinity === CardAffinity.Basic
        ? 'Basic'
        : card.affinity === CardAffinity.Leader
          ? 'Leader'
          : null,
  });

  const getTributeInfo = (card: TributeDef): TributeInfo => ({
    type: 'tribute',
    tributeID: card.defID,
    name: card.name,
  });

  const getReferenceInfo = (
    referenceName:
      | 'scavenge'
      | 'mercenary'
      | 'probability-win'
      | 'probability-lose',
  ): ReferenceInfo => ({
    type: 'reference',
    name: `reference-${referenceName}`,
    referenceName,
  });

  const getGenericBasicInfo = (
    genericBasicName: 'scavenge' | 'mercenary' | 'settlement',
  ): GenericBasicInfo => ({
    type: 'generic-basic',
    name: `generic-basic-${genericBasicName}`,
    genericBasicName,
  });

  const getBackInfo = (cardBack: CardBack): BackInfo => ({
    type: 'back',
    backType: cardBack.type,
  });

  const shouldRender = (name: string) => !cardFilter || name === cardFilter;

  // For array-based sections, preserve original indices for name computation
  // by filtering after computing names
  const filterCards = <T,>(
    cards: T[],
    nameFn: (card: T, index: number) => string,
  ): { card: T; index: number; name: string }[] =>
    cards
      .map((card, i) => ({ card, index: i, name: nameFn(card, i) }))
      .filter(({ name }) => shouldRender(name));

  const filteredAge1 = filterCards(age1cards, cardName);
  const filteredAge2 = filterCards(age2cards, cardName);
  const filteredAge3 = filterCards(age3cards, cardName);
  const filteredBasics = filterCards(basiccards, cardName);
  const filteredTributes = filterCards(tributeCardDefs, tributeName);
  const filteredLeaders = filterCards(leadercards, cardName);
  const filteredBacks = Object.values(cardBackByType).filter((cb) =>
    shouldRender(cardBackName(cb)),
  );

  return (
    <ArtResolutionContext.Provider value={artResolution}>
      <div className="App">
        <Global styles={PrintStyles.global} />
        {filteredAge1.length > 0 && (
          <div id="age1cards" style={containerStyle}>
            {filteredAge1.map(({ card, name }) => (
              <PrintCardContainer
                key={name}
                name={name}
                info={getCardInfo(card)}
              >
                <CardView card={card} />
              </PrintCardContainer>
            ))}
          </div>
        )}
        {filteredAge2.length > 0 && (
          <div id="age2cards" style={containerStyle}>
            {filteredAge2.map(({ card, name }) => (
              <PrintCardContainer
                key={name}
                name={name}
                info={getCardInfo(card)}
              >
                <CardView card={card} />
              </PrintCardContainer>
            ))}
          </div>
        )}
        {filteredAge3.length > 0 && (
          <div id="age3cards" style={containerStyle}>
            {filteredAge3.map(({ card, name }) => (
              <PrintCardContainer
                key={name}
                name={name}
                info={getCardInfo(card)}
              >
                <CardView card={card} />
              </PrintCardContainer>
            ))}
          </div>
        )}
        {filteredBasics.length > 0 && (
          <div id="basiccards" style={containerStyle}>
            {filteredBasics.map(({ card, name }) => (
              <PrintCardContainer
                key={name}
                name={name}
                info={getCardInfo(card)}
              >
                <CardView card={card} />
              </PrintCardContainer>
            ))}
          </div>
        )}
        {filteredTributes.length > 0 && (
          <div id="tributecards" style={containerStyle}>
            {filteredTributes.map(({ card, name }) => (
              <PrintCardContainer
                key={name}
                name={name}
                rotation={CardRotation.LandscapeFront}
                info={getTributeInfo(card)}
              >
                <TributeCardView card={card} />
              </PrintCardContainer>
            ))}
          </div>
        )}
        {filteredLeaders.length > 0 && (
          <div id="leadercards" style={containerStyle}>
            {filteredLeaders.map(({ card, name }) => (
              <PrintCardContainer
                key={name}
                name={name}
                info={getCardInfo(card)}
              >
                <CardView card={card} />
              </PrintCardContainer>
            ))}
          </div>
        )}
        <div id="referencecards" style={containerStyle}>
          {shouldRender('reference-scavenge') && (
            <PrintCardContainer
              name="reference-scavenge"
              rotation={CardRotation.LandscapeFront}
              info={getReferenceInfo('scavenge')}
            >
              <ScavengeReferenceCardView scavengeCards={scavengeCards} />
            </PrintCardContainer>
          )}
          {shouldRender('reference-mercenary') && (
            <PrintCardContainer
              name="reference-mercenary"
              rotation={CardRotation.LandscapeBack}
              info={getReferenceInfo('mercenary')}
            >
              <MercenaryReferenceCardView mercenaryCards={mercenaryCards} />
            </PrintCardContainer>
          )}
          {shouldRender('reference-probability-win') && (
            <PrintCardContainer
              name="reference-probability-win"
              info={getReferenceInfo('probability-win')}
            >
              <ProbabilityReferenceCardView mode="win" />
            </PrintCardContainer>
          )}
          {shouldRender('reference-probability-lose') && (
            <PrintCardContainer
              name="reference-probability-lose"
              info={getReferenceInfo('probability-lose')}
            >
              <ProbabilityReferenceCardView mode="lose" />
            </PrintCardContainer>
          )}
        </div>
        <div id="genericbasiccards" style={containerStyle}>
          {shouldRender('generic-basic-scavenge') && (
            <PrintCardContainer
              name="generic-basic-scavenge"
              info={getGenericBasicInfo('scavenge')}
            >
              <CardView
                card={genericBasics.scavenge}
                overrides={{ genericGainSelf: 'scavenge' }}
              />
            </PrintCardContainer>
          )}
          {shouldRender('generic-basic-mercenary') && (
            <PrintCardContainer
              name="generic-basic-mercenary"
              info={getGenericBasicInfo('mercenary')}
            >
              <CardView
                card={genericBasics.mercenary}
                overrides={{ genericCost: true, genericGainSelf: 'mercenary' }}
              />
            </PrintCardContainer>
          )}
          {shouldRender('generic-basic-settlement') && (
            <PrintCardContainer
              name="generic-basic-settlement"
              info={getGenericBasicInfo('settlement')}
            >
              <CardView card={genericBasics.settlement} />
            </PrintCardContainer>
          )}
        </div>
        {filteredBacks.length > 0 && (
          <div id="cardbacks" style={containerStyle}>
            {filteredBacks.map((cardBack) => (
              <PrintCardContainer
                key={cardBackName(cardBack)}
                name={cardBackName(cardBack)}
                rotation={
                  cardBack.type === 'tribute'
                    ? CardRotation.LandscapeBack
                    : CardRotation.Vertical
                }
                info={getBackInfo(cardBack)}
              >
                <CardBackView card={cardBack} />
              </PrintCardContainer>
            ))}
          </div>
        )}
      </div>
    </ArtResolutionContext.Provider>
  );
}
export default PrintCardsPage;

function cardName(card: CardDef, index: number): string {
  let prefix = `age${card.age}`;
  if (card.affinity === CardAffinity.Leader) {
    prefix = 'leader';
  } else if (card.affinity === CardAffinity.Basic) {
    prefix = `basic${card.age}`;
  }
  return cardNameHelper(prefix, index, card.name);
}
function tributeName(card: TributeDef, index: number): string {
  return cardNameHelper('tribute', index, card.name);
}
function cardBackName(card: CardBack): string {
  return `${card.type}-back`;
}
function cardNameHelper(prefix: string, index: number, name: string): string {
  return `${prefix}-${index + 1}-${name.toLowerCase().replace(/ /g, '_')}`;
}

enum CardRotation {
  Vertical = 'Vertical',
  LandscapeFront = 'LandscapeFront',
  LandscapeBack = 'LandscapeBack',
}

const PrintCardContainer = (props: {
  name: string;
  info: RenderItemInfo;
  rotation?: CardRotation;
  children: React.ReactNode;
}): React.ReactNode => {
  return (
    <div
      css={PrintStyles.cardContainer}
      className="print-card-container"
      data-name={props.name}
      data-info={JSON.stringify(props.info)}
    >
      <div
        css={
          props.rotation === CardRotation.LandscapeFront
            ? PrintStyles.cardSubContainerLandscapeFront
            : props.rotation === CardRotation.LandscapeBack
              ? PrintStyles.cardSubContainerLandscapeBack
              : PrintStyles.cardSubContainer
        }
      >
        {props.children}
      </div>
    </div>
  );
};

const PrintStyles = {
  global: css({
    '@media print': {
      body: {
        backgroundImage: 'none',
        backgroundColor: 'inherit',
      },
      '@page': {
        size: 'letter portrait',
        margin: 0,
      },
    },
  }),
  cardContainer: css({
    position: 'relative',

    // 300 dpi
    width: 816,
    height: 1110,

    backgroundColor: 'black',
  }),
  cardSubContainer: css({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zoom: 7.44,
  }),
  cardSubContainerLandscapeFront: css({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-90deg)',
    zoom: 7.44,
  }),
  cardSubContainerLandscapeBack: css({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(90deg)',
    zoom: 7.44,
  }),
};
